import { MouseEvent, useCallback, useState } from 'react';
import { NodeViewProps, NodeViewWrapper } from '@tiptap/react';
import { TbChevronRight } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';

import { getFilingSourceTruthUrl, isPublicMemoPath } from '@/shared/components/memos/filingSourceNavigation';
import { resolveCorporateLogo } from '@/utilities';

interface FilingSourceCitationAttributes {
    avatar?: string | null;
    company?: string | null;
    filing?: string | null;
    href?: string | null;
    logoUrl?: string | null;
    website?: string | null;
}

interface FilingSourceCitationComponentProps extends NodeViewProps {
    block?: boolean;
}

const getInternalDestination = (href: string) => {
    try {
        const url = new URL(href, window.location.origin);

        return url.origin === window.location.origin ? `${url.pathname}${url.search}${url.hash}` : undefined;
    } catch {
        return undefined;
    }
};

export const FilingSourceCitationComponent = ({ block = false, node }: FilingSourceCitationComponentProps) => {
    const navigate = useNavigate();
    const { avatar, company, filing, href, logoUrl, website } = node.attrs as FilingSourceCitationAttributes;
    const resolvedLogoUrl = resolveCorporateLogo(website ?? undefined, logoUrl ?? undefined);
    const [failedLogoUrl, setFailedLogoUrl] = useState<string>();
    const showLogo = Boolean(resolvedLogoUrl && resolvedLogoUrl !== failedLogoUrl);
    const publicMemo = isPublicMemoPath(window.location.pathname);
    const publicSourceTruthUrl = publicMemo ? getFilingSourceTruthUrl(href) : undefined;
    const canNavigate = publicMemo ? Boolean(publicSourceTruthUrl) : Boolean(href);

    const handleClick = useCallback(
        (event: MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();

            if (!href) {
                return;
            }

            if (publicSourceTruthUrl) {
                window.open(publicSourceTruthUrl, '_blank', 'noopener,noreferrer');

                return;
            }

            if (publicMemo) {
                return;
            }

            const internalDestination = getInternalDestination(href);

            if (internalDestination) {
                navigate(internalDestination);
            }
        },
        [href, navigate, publicMemo, publicSourceTruthUrl],
    );

    return (
        <NodeViewWrapper
            as={block ? 'p' : 'span'}
            className={block ? 'filing-source-citation filing-source-citation-node' : 'filing-source-node'}
            contentEditable={false}
            data-filing-source-citation={block ? 'true' : undefined}
            draggable={false}
        >
            <button
                aria-label={`Open source: ${company || 'SEC Filing'}, ${filing || 'Filing'}`}
                className='filing-source-button'
                data-company={company || 'SEC Filing'}
                data-filing={filing || 'Filing'}
                data-filing-source='true'
                data-logo-url={logoUrl || undefined}
                data-source-url={href || undefined}
                data-website={website || undefined}
                disabled={!canNavigate}
                draggable={false}
                onClick={handleClick}
                type='button'
            >
                <span aria-hidden='true' className='filing-source-avatar'>
                    {showLogo ? (
                        <img
                            alt=''
                            draggable={false}
                            onError={() => setFailedLogoUrl(resolvedLogoUrl)}
                            src={resolvedLogoUrl}
                        />
                    ) : (
                        avatar || 'F'
                    )}
                </span>
                <span className='filing-source-company'>{company || 'SEC Filing'}</span>
                <TbChevronRight aria-hidden='true' className='filing-source-chevron' size='0.875rem' />
                <span className='filing-source-filing'>{filing || 'Filing'}</span>
            </button>
        </NodeViewWrapper>
    );
};
